import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import client from '../api/client';

const DEFAULT_DESIGN = {
  app_name_left: 'trainedby',
  app_name_right: 'RS',
  primary_color: '#FF5C00',
  logo_icon: 'Dumbbell',
  login_hero_image: '',
  login_tagline_top: 'TRAIN SMARTER.',
  login_tagline_bottom: 'ACHIEVE MORE.',
  login_subtitle: 'The complete platform for coaches and athletes to track workouts, nutrition, and progress.',
  heading_font: 'Barlow Condensed',
  body_font: 'Inter',
  border_radius: '0.5',
};

const DesignContext = createContext(null);

function hexToHSL(hex) {
  let r = 0, g = 0, b = 0;
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  } else if (hex.length === 7) {
    r = parseInt(hex.slice(1, 3), 16);
    g = parseInt(hex.slice(3, 5), 16);
    b = parseInt(hex.slice(5, 7), 16);
  }
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: break;
    }
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function applyDesignCSS(design) {
  const root = document.documentElement;
  const color = design.primary_color || '#FF5C00';
  const hsl = hexToHSL(color);
  root.style.setProperty('--primary', hsl);
  root.style.setProperty('--accent', hsl);
  root.style.setProperty('--ring', hsl);
  root.style.setProperty('--chart-1', hsl);
  root.style.setProperty('--radius', `${design.border_radius || 0.5}rem`);

  // Load fonts
  const hf = design.heading_font || 'Barlow Condensed';
  const bf = design.body_font || 'Inter';
  const fontLink = document.getElementById('design-fonts');
  const fontUrl = `https://fonts.googleapis.com/css2?family=${hf.replace(/ /g, '+')}:wght@400;600;700;900&family=${bf.replace(/ /g, '+')}:wght@400;500;600;700&display=swap`;
  if (fontLink) {
    fontLink.href = fontUrl;
  } else {
    const link = document.createElement('link');
    link.id = 'design-fonts';
    link.rel = 'stylesheet';
    link.href = fontUrl;
    document.head.appendChild(link);
  }
  document.body.style.fontFamily = `'${bf}', sans-serif`;

  // Apply heading font via a style tag
  let styleEl = document.getElementById('design-heading-font');
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = 'design-heading-font';
    document.head.appendChild(styleEl);
  }
  styleEl.textContent = `h1,h2,h3,h4,h5,h6{font-family:'${hf}',sans-serif!important}`;
}

export const DesignProvider = ({ children }) => {
  const [design, setDesign] = useState(DEFAULT_DESIGN);
  const [loaded, setLoaded] = useState(false);

  const fetchDesign = useCallback(async () => {
    try {
      const res = await client.get('/design-settings');
      setDesign(prev => ({ ...prev, ...res.data }));
      applyDesignCSS(res.data);
    } catch {
      applyDesignCSS(DEFAULT_DESIGN);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { fetchDesign(); }, [fetchDesign]);

  const updateDesign = useCallback(async (updates) => {
    setDesign(prev => {
      const merged = { ...prev, ...updates };
      applyDesignCSS(merged);
      return merged;
    });
    await client.put('/design-settings', updates);
  }, []);

  const value = useMemo(() => ({ design, updateDesign, fetchDesign, loaded }), [design, updateDesign, fetchDesign, loaded]);

  return (
    <DesignContext.Provider value={value}>
      {children}
    </DesignContext.Provider>
  );
};

export const useDesign = () => {
  const ctx = useContext(DesignContext);
  if (!ctx) throw new Error('useDesign must be used within DesignProvider');
  return ctx;
};
